import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { Search, Loader, Calendar as CalendarIcon, Clock, Users, GraduationCap, CreditCard, FileText, BookOpen, Scissors } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import { useMobile } from '../hooks/useMobile';

// Import dashboard components
import KPICard from '../components/Dashboard/KPICard';
import QuickActionsBar from '../components/Dashboard/QuickActionsBar';
import TherapyQuickActionsBar from '../components/Dashboard/TherapyQuickActionsBar';
import TherapyDashboardPanel from '../components/Dashboard/TherapyDashboardPanel';
import ClientQuickAddModal from '../components/Dashboard/ClientQuickAddModal';
import AddNoteBottomSheet from '../components/Dashboard/AddNoteBottomSheet';
import TherapyTodayCard from '../components/Dashboard/TherapyTodayCard';
import TodayTimeline from '../components/Dashboard/TodayTimeline';
import StudentSearchModal from '../components/Dashboard/StudentSearchModal';
import UpcomingMeetingsList from '../components/Dashboard/UpcomingMeetingsList';
import UpcomingTreatmentsTable from '../components/Dashboard/UpcomingTreatmentsTable';
import UpcomingLessonsList from '../components/Dashboard/UpcomingLessonsList';
import RecentActivityList from '../components/Dashboard/RecentActivityList';
import DashboardCharts from '../components/Dashboard/DashboardCharts';
import { useToastContext } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import ExportPdfButton from '../components/ExportPdfButton';
import { getTemplateById } from '../data/surveyTemplates';
import ManagerStaffTimeline from '../components/Dashboard/ManagerStaffTimeline';
import ManagerGrowthMetrics from '../components/Dashboard/ManagerGrowthMetrics';
import LearningDashboardPage from './LearningDashboardPage';

// ─── Lazy chunks ────────────────────────────────────────────────────────────
// Center-type-specific pieces: learning/sport centers never render these,
// so they stay out of the main bundle (it was over its 400KB gzip budget).
const HairTransplantDashboardPage = lazy(() => import('./HairTransplantDashboardPage'));
const ScheduleTreatmentModal = lazy(() => import('../components/TransplantCrm/ScheduleTreatmentModal'));
const UploadDocumentWizardModal = lazy(() => import('../components/Dashboard/UploadDocumentWizardModal'));
// All these transitively pull either react-quill (RichTextEditor) or recharts.
// Lazy-loading them keeps those vendor chunks out of the dashboard's eager
// dependency graph, so first paint doesn't fetch editors/charts the user
// hasn't asked for yet.

// Only therapy/hair_transplant admins render this. Learning admins and
// teachers never see it.
const ManagerWorkerActivityChart = lazy(() => import(
  /* webpackChunkName: "manager-worker-activity-chart" */
  '../components/Dashboard/ManagerWorkerActivityChart'
));
const GuestMeetingModal = lazy(() => import(
  /* webpackChunkName: "dashboard-guest-meeting-modal" */
  '../components/Dashboard/GuestMeetingModal'
));
// Prefetch — these modals are opened often enough from the dashboard
// (quick "Add lesson" / "Add meeting" actions) that pre-pulling them
// during idle is worth the bandwidth. Browser still skips on data-saver.
const LessonModal = lazy(() => import(
  /* webpackChunkName: "dashboard-lesson-modal" */
  /* webpackPrefetch: true */
  '../components/Dashboard/LessonModal'
));
const MeetingModal = lazy(() => import(
  /* webpackChunkName: "dashboard-meeting-modal" */
  /* webpackPrefetch: true */
  '../components/Dashboard/MeetingModal'
));

/**
 * Public export — delegates each centerType to a purpose-built dashboard.
 * The conditional render happens BEFORE any hooks in DashboardPageImpl
 * are called, so the rules of hooks are respected across center types.
 */
export default function DashboardPage({ teacherFilter = null, isTeacherView = false }) {
  const centerType = useAuthStore((s) => s.centerType);
  if (centerType === 'learning') {
    return <LearningDashboardPage teacherFilter={teacherFilter} isTeacherView={isTeacherView} />;
  }
  if (centerType === 'hair_transplant' && !teacherFilter && !isTeacherView) {
    return (
      <Suspense fallback={null}>
        <HairTransplantDashboardPage />
      </Suspense>
    );
  }
  return <DashboardPageImpl teacherFilter={teacherFilter} isTeacherView={isTeacherView} />;
}

function DashboardPageImpl({ teacherFilter = null, isTeacherView = false }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { user, centerType } = useAuthStore();
  const isClinicCenter = centerType === 'therapy' || centerType === 'hair_transplant';
  const isMobile = useMobile();
  const isTablet = useMobile(1024) && !isMobile;
  const { success, error } = useToastContext();
  const confirm = useConfirm();

  // Data state
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);
  const [managerOverview, setManagerOverview] = useState(null);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [clientMap, setClientMap] = useState({});

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isMeetingsLoading, setIsMeetingsLoading] = useState(false);
  const [isLessonsLoading, setIsLessonsLoading] = useState(false);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);

  // Modal states
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showStudentSearchModal, setShowStudentSearchModal] = useState(false);
  const [showScheduleTreatmentModal, setShowScheduleTreatmentModal] = useState(false);
  const [showGuestMeetingModal, setShowGuestMeetingModal] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showUploadDocWizard, setShowUploadDocWizard] = useState(false);
  const [triggerExportPdf, setTriggerExportPdf] = useState(0);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  // Therapy: AddNoteBottomSheet state + preselection
  const [showAddNoteSheet, setShowAddNoteSheet] = useState(false);
  const [noteSheetPreselected, setNoteSheetPreselected] = useState(null);

  // Global search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Compute today's schedule summary for therapy centers
  const todaySchedule = useMemo(() => {
    if (!isClinicCenter) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    const todayMeetings = upcomingMeetings.filter(m => {
      const meetingDate = new Date(m.date);
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate.getTime() === today.getTime();
    });

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const nextMeeting = todayMeetings
      .filter(m => m.time && m.time > currentTime)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''))[0];

    // Count unique clients with meetings this week
    const clientIds = new Set(upcomingMeetings.map(m => m.studentId).filter(Boolean));

    return {
      meetingsToday: todayMeetings.length,
      nextMeetingTime: nextMeeting?.time || null,
      clientsThisWeek: clientIds.size
    };
  }, [upcomingMeetings, centerType]);

  // Today's meetings list for QuickActionsBar (therapy only)
  const todayMeetingsList = useMemo(() => {
    if (!isClinicCenter) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return upcomingMeetings
      .filter(m => {
        const d = new Date(m.date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() === today.getTime();
      })
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }, [upcomingMeetings, centerType]);

  // Past meetings (last 7 days, including today) whose `content` is still
  // empty or has empty JSON notes. These are the "you owe a note" items
  // that drive the dashboard's pendingNotes KPI and the Add-Note shortcut.
  const pendingNotes = useMemo(() => {
    if (centerType !== 'therapy') return [];
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const hasNotes = (content) => {
      if (!content) return false;
      const trimmed = String(content).trim();
      if (!trimmed) return false;
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          const notes = (parsed.notes || '').toString().trim();
          const nextSteps = (parsed.nextSteps || '').toString().trim();
          return Boolean(notes || nextSteps);
        }
      } catch {
        // plain-text content
        return trimmed.length > 0;
      }
      return false;
    };

    return upcomingMeetings
      .filter(m => {
        if (m.status === 'cancelled' || m.status === 'no_show') return false;
        const d = new Date(m.date);
        if (Number.isNaN(d.getTime())) return false;
        // Past or today but already past `time`
        const sameDay = d.toDateString() === now.toDateString();
        if (sameDay && m.time) {
          const [h, mm] = m.time.split(':').map(Number);
          const meetingMs = d.getTime() + (h * 60 + (mm || 0)) * 60 * 1000;
          if (meetingMs > now.getTime()) return false;
        } else if (d > now) {
          return false;
        }
        if (d < sevenDaysAgo) return false;
        return !hasNotes(m.content);
      })
      .sort((a, b) => {
        // most recent first — that's usually the one a therapist wants to
        // write up right after the session
        const ka = `${a.date} ${a.time || ''}`;
        const kb = `${b.date} ${b.time || ''}`;
        return kb.localeCompare(ka);
      });
  }, [upcomingMeetings, centerType]);

  // Upcoming treatments (hair_transplant only) - meetings with type='treatment' in next 7 days
  const upcomingTreatments = useMemo(() => {
    if (centerType !== 'hair_transplant') return [];
    return upcomingMeetings.filter(m => m.type === 'treatment' || m.type === 'Treatment');
  }, [upcomingMeetings, centerType]);

  // Worker name map (for specialist column) - built from teachers list
  const workerMap = useMemo(() => {
    const map = {};
    teachers.forEach(w => {
      const name = `${w.firstName || ''} ${w.lastName || ''}`.trim() || w.name || w.email || '';
      if (name) map[w.id] = name;
    });
    return map;
  }, [teachers]);

  // Active surveys for QuickActionsBar - inject informed consent template for therapy centers if not present
  const activeSurveys = useMemo(() => {
    const active = surveys.filter(s => s.isActive);
    if (isClinicCenter) {
      const hasInformedConsent = active.some(s => s.templateId === 'informed_consent' || s.id === 'informed_consent' || s.title === 'טופס הסכמה מדעת');
      if (!hasInformedConsent) {
        const template = getTemplateById('informed_consent');
        if (template) {
          active.unshift({ ...template, id: '__template_informed_consent', isActive: true });
        }
      }
    }
    return active;
  }, [surveys, centerType]);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, [teacherFilter]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      // First, fetch base data (students, groups, etc.)
      const [
        studentsRes,
        groupsRes,
        surveysRes,
        activitiesRes,
        meetingTypesRes,
        roomsRes,
        teachersRes,
        jibayaRes
      ] = await Promise.allSettled([
        api.get('/students?limit=100000&enrich=0'),
        api.get('/groups'),
        api.get('/surveys'),
        api.get('/audit-logs/recent?limit=30'),
        api.get('/meeting-types'),
        api.get('/rooms'),
        api.get('/teachers'),
        !isClinicCenter ? api.get('/jibaya').catch(() => []) : Promise.resolve([])
      ]);

      // Fetch transplant clients for name resolution in meetings
      let transplantClientsData = [];
      if (isClinicCenter) {
        try {
          const tcRes = await api.get('/transplant-clients?limit=100');
          transplantClientsData = Array.isArray(tcRes) ? tcRes : (tcRes?.data || []);
        } catch (err) {
          console.error('Error fetching transplant clients:', err);
        }
      }

      // Fetch treatment types for hair_transplant dashboard filter badges
      if (centerType === 'hair_transplant') {
        try {
          const typesRes = await api.get('/settings/treatment-types');
          const types = typesRes?.types || (Array.isArray(typesRes) ? typesRes : []);
          setTreatmentTypes(types.length > 0 ? types : ['FUE', 'FUT', 'DHI', 'PRP', 'consultation']);
        } catch (err) {
          setTreatmentTypes(['FUE', 'FUT', 'DHI', 'PRP', 'consultation']);
        }
      }

      // Process students
      let studentData = [];
      if (studentsRes.status === 'fulfilled') {
        studentData = Array.isArray(studentsRes.value) ? studentsRes.value : (studentsRes.value?.data || []);
        setStudents(studentData);
      }

      // Build client name map for hair_transplant dashboard (clients + students)
      if (centerType === 'hair_transplant') {
        const map = {};
        transplantClientsData.forEach(c => {
          map[c.id] = c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.id;
        });
        studentData.forEach(s => {
          map[s.id] = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.fullName || s.id;
        });
        setClientMap(map);
      }

      // Process groups
      let groupData = [];
      if (groupsRes.status === 'fulfilled') {
        groupData = Array.isArray(groupsRes.value) ? groupsRes.value : (groupsRes.value?.data || []);
        setGroups(groupData);
      }

      // Process surveys
      if (surveysRes.status === 'fulfilled') {
        const surveyData = Array.isArray(surveysRes.value) ? surveysRes.value : (surveysRes.value?.data || []);
        setSurveys(surveyData);
      }

      // Process activities
      if (activitiesRes.status === 'fulfilled') {
        const activityData = Array.isArray(activitiesRes.value) ? activitiesRes.value : (activitiesRes.value?.data || []);
        setRecentActivities(activityData);
      }

      // Process meeting types
      if (meetingTypesRes.status === 'fulfilled') {
        const typesData = Array.isArray(meetingTypesRes.value) ? meetingTypesRes.value : (meetingTypesRes.value?.data || []);
        setMeetingTypes(typesData);
      }

      // Process rooms
      if (roomsRes.status === 'fulfilled') {
        const roomsData = Array.isArray(roomsRes.value) ? roomsRes.value : (roomsRes.value?.data || []);
        setRooms(roomsData);
      }

      // Process teachers
      if (teachersRes.status === 'fulfilled') {
        const teachersData = Array.isArray(teachersRes.value) ? teachersRes.value : (teachersRes.value?.data || []);
        setTeachers(teachersData);
      }

      // Process jibaya (invoices) for payments due count
      if (jibayaRes.status === 'fulfilled') {
        const jibayaData = Array.isArray(jibayaRes.value) ? jibayaRes.value : (jibayaRes.value?.data || []);
        const pendingCount = jibayaData.filter(j => (j.remainingAmount || 0) > 0).length;
        setPendingInvoicesCount(pendingCount);
      }

      // Now fetch meetings and lessons with student/group data available
      const [meetingsRes, lessonsRes] = await Promise.allSettled([
        fetchUpcomingMeetings(studentData, groupData, transplantClientsData),
        !isClinicCenter ? fetchUpcomingLessons(studentData, groupData) : Promise.resolve([])
      ]);

      // Process meetings
      if (meetingsRes.status === 'fulfilled') {
        setUpcomingMeetings(meetingsRes.value || []);
      }

      // Process lessons
      if (lessonsRes.status === 'fulfilled' && !isClinicCenter) {
        setUpcomingLessons(lessonsRes.value || []);
      }
      // Fetch manager overview for hair_transplant/therapy centers (managers only)
      if (isClinicCenter && !isTeacherView) {
        try {
          const overviewRes = await api.get('/clinic-analytics/manager-overview');
          const overviewData = overviewRes?.data || overviewRes;
          setManagerOverview(overviewData);
        } catch (e) {
          console.warn('Manager overview not available:', e);
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      error(t('dashboard.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch upcoming meetings (today to +7 days)
  const fetchUpcomingMeetings = async (studentsData = students, groupsData = groups, transplantClientsData = []) => {
    setIsMeetingsLoading(true);
    try {
      const response = await api.get('/meetings');
      const allMeetings = Array.isArray(response) ? response : (response?.data || []);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sevenDaysLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      let filtered = allMeetings.filter(m => {
        const date = new Date(m.date);
        return date >= today && date <= sevenDaysLater;
      });

      // Apply teacher filter if needed
      if (teacherFilter?.teacherId) {
        // Get teacher's students
        const teacherGroups = groupsData.filter(g => g.teacherId === teacherFilter.teacherId || (g.teacherIds || []).includes(teacherFilter.teacherId));
        const studentIds = new Set();
        for (const group of teacherGroups) {
          try {
            const membersRes = await api.get(`/groups/${group.id}/students`);
            const members = Array.isArray(membersRes) ? membersRes : (membersRes?.data || []);
            members.forEach(m => studentIds.add(m.studentId || m.id));
          } catch (err) {
            console.error('Error fetching group members:', err);
          }
        }
        filtered = filtered.filter(m => studentIds.has(m.studentId));
      }

      // Enrich with student/client names
      const enriched = filtered.map(meeting => {
        const student = studentsData.find(s => s.id === meeting.studentId);
        if (student) {
          return { ...meeting, studentName: `${student.firstName || ''} ${student.lastName || ''}`.trim() || t('common.unknown') };
        }
        const transplantClient = transplantClientsData.find(c => c.id === meeting.studentId);
        if (transplantClient) {
          return { ...meeting, studentName: transplantClient.fullName || t('common.unknown') };
        }
        return { ...meeting, studentName: t('common.unknown') };
      });

      return enriched.sort((a, b) => new Date(a.date) - new Date(b.date));
    } catch (err) {
      console.error('Error fetching meetings:', err);
      return [];
    } finally {
      setIsMeetingsLoading(false);
    }
  };

  // Fetch upcoming lessons (today to +7 days)
  const fetchUpcomingLessons = async (studentsData = students, groupsData = groups) => {
    setIsLessonsLoading(true);
    try {
      const response = await api.get('/lessons');
      const allLessons = Array.isArray(response) ? response : (response?.data || []);

      // Normalize Firestore Timestamp dates to ISO strings
      allLessons.forEach(l => {
        if (l.scheduledDate && l.scheduledDate._seconds) {
          l.scheduledDate = new Date(l.scheduledDate._seconds * 1000).toISOString();
        }
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sevenDaysLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      let filtered = allLessons.filter(l => {
        const date = new Date(l.scheduledDate || l.date);
        return date >= today && date <= sevenDaysLater;
      });

      // Apply teacher filter if needed
      if (teacherFilter?.teacherId) {
        const teacherGroups = groupsData.filter(g => g.teacherId === teacherFilter.teacherId || (g.teacherIds || []).includes(teacherFilter.teacherId));
        const groupIds = teacherGroups.map(g => g.id);
        const studentIds = new Set();
        for (const group of teacherGroups) {
          try {
            const membersRes = await api.get(`/groups/${group.id}/students`);
            const members = Array.isArray(membersRes) ? membersRes : (membersRes?.data || []);
            members.forEach(m => studentIds.add(m.studentId || m.id));
          } catch (err) {
            console.error('Error fetching group members:', err);
          }
        }
        filtered = filtered.filter(l => groupIds.includes(l.groupId) || studentIds.has(l.studentId));
      }

      // Enrich with student/group names
      const enriched = filtered.map(lesson => {
        if (lesson.groupId) {
          const group = groupsData.find(g => g.id === lesson.groupId);
          return {
            ...lesson,
            groupName: group?.name || t('groups.unknownGroup')
          };
        } else {
          const student = studentsData.find(s => s.id === lesson.studentId);
          return {
            ...lesson,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : t('common.unknown')
          };
        }
      });

      return enriched.sort((a, b) => new Date(a.scheduledDate || a.date) - new Date(b.scheduledDate || b.date));
    } catch (err) {
      console.error('Error fetching lessons:', err);
      return [];
    } finally {
      setIsLessonsLoading(false);
    }
  };

  // Action handlers
  const handleAddLesson = () => {
    setEditingLesson(null);
    setShowLessonModal(true);
  };

  const handleAddMeeting = () => {
    setEditingMeeting(null);
    setShowMeetingModal(true);
  };

  const handleSurveyClick = (survey) => {
    setSelectedSurvey(survey);
    setShowStudentSearchModal(true);
  };

  const isInformedConsentSurvey = (survey) => {
    if (!survey) return false;
    return survey.templateId === 'informed_consent' || survey.id === 'informed_consent' || survey.id === '__template_informed_consent' || survey.title === 'טופס הסכמה מדעת';
  };

  const handleStudentSelect = (student) => {
    setShowStudentSearchModal(false);
    if (selectedSurvey) {
      // Survey-response route is unchanged by the per-center refactor.
      navigate(`/student/${student.id}/survey/${selectedSurvey.id}`);
    } else {
      const base = centerType === 'learning' ? '/students' : '/clients';
      navigate(`${base}/${student.id}`);
    }
  };

  const handleSkipClient = () => {
    setShowStudentSearchModal(false);
    if (selectedSurvey) {
      navigate(`/survey/${selectedSurvey.id}`);
    }
  };

  const handleSaveLesson = async (lessonData) => {
    try {
      if (editingLesson) {
        await api.put(`/lessons/${editingLesson.id}`, lessonData);
        success(t('lessons.lessonUpdated'));
      } else {
        // Determine endpoint based on whether it's a group or student lesson
        if (lessonData.groupId) {
          await api.post(`/lessons?groupId=${lessonData.groupId}`, lessonData);
        } else {
          await api.post('/lessons', lessonData);
        }
        success(t('lessons.lessonCreated'));
      }
      setShowLessonModal(false);
      setEditingLesson(null);
      // Refresh lessons
      const lessons = await fetchUpcomingLessons();
      setUpcomingLessons(lessons);
    } catch (err) {
      console.error('Error saving lesson:', err);
      error(t('lessons.saveFailed'));
      throw err;
    }
  };

  const handleSaveMeeting = async (meetingData) => {
    try {
      if (editingMeeting) {
        await api.put(`/meetings/${editingMeeting.id}`, meetingData);
        success(t('calendar.meetingUpdatedSuccess'));
      } else {
        await api.post(`/meetings/student/${meetingData.studentId}`, meetingData);
        success(t('calendar.meetingCreatedSuccess'));
      }
      setShowMeetingModal(false);
      setEditingMeeting(null);
      // Refresh meetings
      const meetings = await fetchUpcomingMeetings();
      setUpcomingMeetings(meetings);
    } catch (err) {
      console.error('Error saving meeting:', err);
      error(t('meetings.saveFailed'));
      throw err;
    }
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setShowLessonModal(true);
  };

  const handleDeleteLesson = async (lesson) => {
    const ok = await confirm({
      title: t('common.delete'),
      message: t('lessons.confirmDelete'),
      confirmText: t('common.delete'),
      isDangerous: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/lessons/${lesson.id}`);
      success(t('lessons.lessonDeleted'));
      const lessons = await fetchUpcomingLessons();
      setUpcomingLessons(lessons);
    } catch (err) {
      console.error('Error deleting lesson:', err);
      error(t('lessons.deleteError'));
    }
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setShowMeetingModal(true);
  };

  const handleCancelMeeting = async (meeting) => {
    const ok = await confirm({
      title: t('common.cancel'),
      message: t('meetings.confirmCancel'),
      confirmText: t('common.confirm'),
      isDangerous: true,
    });
    if (!ok) return;
    try {
      await api.put(`/meetings/${meeting.id}`, { ...meeting, status: 'cancelled' });
      success(t('meetings.cancelled'));
      const meetings = await fetchUpcomingMeetings();
      setUpcomingMeetings(meetings);
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      error(t('meetings.cancelFailed'));
    }
  };

  // Global search functionality
  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const query = searchQuery.toLowerCase();
        const results = [];

        // Search students
        students.forEach(student => {
          const name = `${student.firstName || ''} ${student.lastName || ''}`.toLowerCase();
          if (
            name.includes(query) ||
            student.email?.toLowerCase().includes(query) ||
            student.phone?.toLowerCase().includes(query)
          ) {
            results.push({
              type: 'student',
              id: student.id,
              name: `${student.firstName || ''} ${student.lastName || ''}`,
              subtitle: student.email || student.phone,
              path: `${centerType === 'learning' ? '/students' : '/clients'}/${student.id}`
            });
          }
        });

        // Search teachers
        teachers.forEach(teacher => {
          const name = teacher.name || [teacher.firstName, teacher.lastName].filter(Boolean).join(' ');
          if (
            name.toLowerCase().includes(query) ||
            teacher.email?.toLowerCase().includes(query)
          ) {
            results.push({
              type: 'teacher',
              id: teacher.id,
              name,
              subtitle: teacher.email,
              // The staff profile page; there is no /teachers/:id route.
              path: `/manager/teachers/${teacher.id}`
            });
          }
        });

        // Search groups
        groups.forEach(group => {
          if (group.name?.toLowerCase().includes(query)) {
            results.push({
              type: 'group',
              id: group.id,
              name: group.name,
              subtitle: `${group.subject || ''} - ${group.level || ''}`,
              path: `/groups/${group.id}`
            });
          }
        });

        setSearchResults(results.slice(0, 10));
        setShowSearchResults(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, students, teachers, groups]);

  // Compute KPI data
  const kpiData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isClinicCenter) {
      const todayMeetings = upcomingMeetings.filter(m => {
        const d = new Date(m.date); d.setHours(0, 0, 0, 0);
        return d.getTime() === today.getTime();
      });
      const activeSurveys = surveys.filter(s => s.isActive);

      if (centerType === 'hair_transplant') {
        const todayTreatments = todayMeetings.filter(m => m.type === 'treatment' || m.type === 'Treatment');
        const upcomingTreatments = upcomingMeetings.filter(m =>
          (m.type === 'treatment' || m.type === 'Treatment') && (m.status || 'scheduled') !== 'cancelled'
        );
        return {
          card1: { icon: Users, label: t('dashboard.totalClients'), value: students.length, iconColor: '#0ea5e9', iconBg: 'rgba(14, 165, 233, 0.1)', link: '/students' },
          card2: { icon: Scissors, label: t('dashboard.todayTreatments'), value: todayTreatments.length, iconColor: '#6366f1', iconBg: 'rgba(99, 102, 241, 0.1)', link: '/treatments' },
          card3: { icon: CalendarIcon, label: t('dashboard.upcomingTreatments'), value: upcomingTreatments.length, iconColor: '#8b5cf6', iconBg: 'rgba(139, 92, 246, 0.1)', link: '/treatments' },
          card4: { icon: FileText, label: t('dashboard.pendingSurveys'), value: activeSurveys.length, iconColor: '#f59e0b', iconBg: 'rgba(245, 158, 11, 0.1)' },
        };
      }

      return {
        card1: { icon: Users, label: t('dashboard.totalClients'), value: students.length, iconColor: '#0ea5e9', iconBg: 'rgba(14, 165, 233, 0.1)' },
        card2: { icon: CalendarIcon, label: t('dashboard.todayMeetings'), value: todayMeetings.length, iconColor: '#6366f1', iconBg: 'rgba(99, 102, 241, 0.1)' },
        card3: { icon: FileText, label: t('dashboard.pendingSurveys'), value: activeSurveys.length, iconColor: '#8b5cf6', iconBg: 'rgba(139, 92, 246, 0.1)' },
      };
    } else {
      const todayLessons = upcomingLessons.filter(l => {
        const d = new Date(l.scheduledDate || l.date); d.setHours(0, 0, 0, 0);
        return d.getTime() === today.getTime();
      });
      const activeGroups = groups.filter(g => g.isActive !== false);
      return {
        card1: { icon: GraduationCap, label: t('dashboard.totalStudents'), value: students.length, iconColor: '#0ea5e9', iconBg: 'rgba(14, 165, 233, 0.1)' },
        card2: { icon: Users, label: t('dashboard.activeGroups'), value: activeGroups.length, iconColor: '#10b981', iconBg: 'rgba(16, 185, 129, 0.1)' },
        card3: { icon: BookOpen, label: t('dashboard.todayLessons'), value: todayLessons.length, iconColor: '#8b5cf6', iconBg: 'rgba(139, 92, 246, 0.1)' },
        card4: { icon: CreditCard, label: t('dashboard.paymentsDue'), value: pendingInvoicesCount, iconColor: '#f59e0b', iconBg: 'rgba(245, 158, 11, 0.1)' },
      };
    }
  }, [students, groups, surveys, upcomingMeetings, upcomingLessons, centerType, pendingInvoicesCount, t]);

  // Loading skeleton
  if (isLoading) {
    return (
      <div style={{
        padding: isMobile ? '1rem' : '2rem',
        maxWidth: '1600px',
        margin: '0 auto',
        minHeight: '400px',
      }}>
        {/* KPI Skeleton */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '0.5rem' : '0.75rem',
          marginBottom: '1.25rem',
        }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              height: '64px',
              borderRadius: '10px',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          ))}
        </div>
        {/* Quick Actions Skeleton */}
        <div style={{ height: '100px', borderRadius: '12px', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', marginBottom: '1.5rem', animation: 'pulse 1.5s ease-in-out infinite' }} />
        {/* Content Skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
          <div style={{ height: '300px', borderRadius: '12px', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: '300px', borderRadius: '12px', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '0.75rem' : '2rem',
      paddingBottom: isMobile ? '5rem' : '2rem',
      maxWidth: '1600px',
      margin: '0 auto',
      position: 'relative',
      minHeight: '400px',
    }}>
      {/* Row 0 (therapy only): greeting + next-up / pending-notes hero card.
         Lands above KPIs on mobile to give the therapist a single-tap
         path into the most likely next action. */}
      {centerType === 'therapy' && (
        <TherapyTodayCard
          todayMeetings={todayMeetingsList}
          pendingNotes={pendingNotes}
          isMobile={isMobile}
          isTablet={isTablet}
          userName={user?.firstName || user?.name || ''}
          onScheduleNewMeeting={handleAddMeeting}
          onOpenMeeting={handleEditMeeting}
          onAddNoteForMeeting={(m) => {
            setNoteSheetPreselected(m || null);
            setShowAddNoteSheet(true);
          }}
        />
      )}

      {/* Row 1: KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : (kpiData.card4 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'),
        gap: isMobile ? '0.5rem' : '0.75rem',
        marginBottom: '1rem',
      }}>
        <KPICard {...kpiData.card1} isRTL={isRTL} onClick={() => navigate(kpiData.card1?.link || '/students')} />
        <KPICard {...kpiData.card2} isRTL={isRTL} onClick={() => navigate(kpiData.card2?.link || (isClinicCenter ? '/calendar' : '/groups'))} />
        <KPICard {...kpiData.card3} isRTL={isRTL} onClick={() => navigate(kpiData.card3?.link || (isClinicCenter ? '/surveys' : '/calendar'))} />
        {kpiData.card4 && <KPICard {...kpiData.card4} isRTL={isRTL} onClick={kpiData.card4?.link ? () => navigate(kpiData.card4.link) : undefined} />}
      </div>

      {/* Row 2: Quick Actions Bar */}
      {centerType === 'therapy' ? (
        <TherapyQuickActionsBar
          onNewMeeting={handleAddMeeting}
          onNewClient={() => setShowNewClientModal(true)}
          onNewGuestMeeting={() => setShowGuestMeetingModal(true)}
          onSendSurvey={() => navigate('/surveys')}
          onUploadDocument={() => setShowUploadDocWizard(true)}
          onAddNote={() => {
            // If there's a single past-today meeting that still has no notes,
            // preselect it so the therapist is one tap away. Otherwise open
            // the sheet with the today list and let them pick.
            const noteable = pendingNotes[0] || null;
            setNoteSheetPreselected(noteable);
            setShowAddNoteSheet(true);
          }}
        />
      ) : (
        <QuickActionsBar
          centerType={centerType}
          onAddLesson={handleAddLesson}
          onAddMeeting={handleAddMeeting}
          onSearchStudent={() => setShowStudentSearchModal(true)}
          onScheduleTreatment={() => setShowScheduleTreatmentModal(true)}
          onUploadDocument={() => setShowUploadDocWizard(true)}
          isMobile={isMobile}
          isRTL={isRTL}
          surveys={activeSurveys}
          todayMeetings={todayMeetingsList}
          onSurveyClick={handleSurveyClick}
          onEditMeeting={handleEditMeeting}
          onExportPdf={() => setTriggerExportPdf(n => n + 1)}
        />
      )}

      {/* Row 2.2: Therapy dashboard panel (meetings/clients widgets) */}
      {centerType === 'therapy' && (
        <TherapyDashboardPanel
          todayMeetings={todayMeetingsList}
          upcomingMeetings={upcomingMeetings}
          clients={students}
          pendingNotes={pendingNotes}
          onScheduleNewMeeting={handleAddMeeting}
          onAddNoteForMeeting={(meeting) => {
            setNoteSheetPreselected(meeting || null);
            setShowAddNoteSheet(true);
          }}
        />
      )}

      {/* Row 2.5: Upcoming Treatments Table (hair_transplant only) */}
      {centerType === 'hair_transplant' && (
        <UpcomingTreatmentsTable
          treatments={upcomingTreatments}
          treatmentTypes={treatmentTypes}
          clientMap={clientMap}
          workerMap={workerMap}
          isRTL={isRTL}
          isMobile={isMobile}
          isTablet={isTablet}
          onRowClick={(tx) => navigate(`/clients/${tx.studentId || tx.transplantClientId}`)}
        />
      )}

      {/* Row 3: Global Search */}
      <div style={{ position: 'relative', maxWidth: '600px', marginBottom: '1.5rem' }}>
        <Search size={18} style={{
          position: 'absolute',
          left: isRTL ? 'auto' : '1rem',
          right: isRTL ? '1rem' : 'auto',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--color-text-secondary)',
          pointerEvents: 'none',
        }} />
        <input
          type="text"
          placeholder={t('dashboard.searchPlaceholderClinic')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-primary-dark)';
            e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
            searchQuery && setShowSearchResults(true);
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--color-border)';
            e.target.style.boxShadow = 'none';
            setTimeout(() => setShowSearchResults(false), 200);
          }}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            paddingLeft: isRTL ? '1rem' : '3rem',
            paddingRight: isRTL ? '3rem' : '1rem',
            border: '1px solid var(--color-border)',
            borderRadius: '10px',
            fontSize: '0.9375rem',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        />
        {isSearching && (
          <Loader size={16} style={{
            position: 'absolute',
            right: isRTL ? 'auto' : '1rem',
            left: isRTL ? '1rem' : 'auto',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--color-primary-dark)',
            animation: 'spin 1s linear infinite',
          }} />
        )}
        {showSearchResults && searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            left: 0, right: 0,
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 100,
          }}>
            {searchResults.map((result) => (
              <div
                key={`${result.type}-${result.id}`}
                onClick={() => navigate(result.path)}
                style={{
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--color-border)',
                  transition: 'background-color 0.15s',
                  direction: isRTL ? 'rtl' : 'ltr',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-background)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                  {result.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                  {result.subtitle}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Row 4: Two-Column Layout - Timeline + Lists */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '1.5rem',
        marginBottom: '1.5rem',
        minHeight: '300px',
      }}>
        {/* Left: Today's Timeline */}
        <TodayTimeline
          meetings={upcomingMeetings}
          lessons={upcomingLessons}
          centerType={centerType}
          isRTL={isRTL}
          isMobile={isMobile}
          onEventClick={(event) => {
            if (event.type === 'meeting') handleEditMeeting(event.data);
            else if (event.type === 'lesson') handleEditLesson(event.data);
          }}
        />

        {/* Right: Upcoming List */}
        {!isClinicCenter ? (
          <UpcomingLessonsList
            lessons={upcomingLessons}
            onEdit={handleEditLesson}
            onDelete={handleDeleteLesson}
            isLoading={isLessonsLoading}
          />
        ) : (
          <UpcomingMeetingsList
            meetings={upcomingMeetings}
            onEdit={handleEditMeeting}
            onCancel={handleCancelMeeting}
            isLoading={isMeetingsLoading}
          />
        )}
      </div>

      {/* Row 5: learning gets a second meetings panel here, therapy/hair
         skip the duplicate (Row 4 already shows UpcomingMeetingsList for
         clinic centers — therapists were getting the same data three
         times before this cleanup). */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile || isClinicCenter ? '1fr' : '1fr 1fr',
        gap: '1.5rem',
        marginBottom: '2rem',
        minHeight: '250px',
      }}>
        {!isClinicCenter && (
          <UpcomingMeetingsList
            meetings={upcomingMeetings}
            onEdit={handleEditMeeting}
            onCancel={handleCancelMeeting}
            isLoading={isMeetingsLoading}
          />
        )}

        <RecentActivityList
          activities={recentActivities}
          isLoading={isActivitiesLoading}
        />
      </div>

      {/* Row 6: Analytics Charts */}
      <div style={{ marginBottom: '2rem' }}>
        <DashboardCharts
          meetings={upcomingMeetings}
          lessons={upcomingLessons || []}
          payments={[]}
          students={students}
          isTherapy={isClinicCenter}
        />
      </div>

      {/* Manager Analytics Sections (clinic centers only) */}
      {isClinicCenter && managerOverview && !isTeacherView && (
        <>
          {/* Staff Timeline */}
          <ManagerStaffTimeline
            staffSchedule={managerOverview.staffSchedule}
            isRTL={isRTL}
            isMobile={isMobile}
            t={t}
          />

          {/* Worker Activity + Service Distribution */}
          <Suspense fallback={null}>
            <ManagerWorkerActivityChart
              workerComparison={managerOverview.workerComparison}
              serviceDistribution={managerOverview.serviceDistribution}
              dailyVolume={managerOverview.dailyVolume}
              isRTL={isRTL}
              isMobile={isMobile}
              t={t}
            />
          </Suspense>

          {/* Growth Metrics */}
          <ManagerGrowthMetrics
            growth={managerOverview.growth}
            isRTL={isRTL}
            isMobile={isMobile}
            t={t}
          />
        </>
      )}

      {/* Export PDF (triggered from Quick Actions) */}
      <ExportPdfButton
        reportType="dashboard"
        variant="outline"
        trigger={triggerExportPdf}
        style={{ display: 'none' }}
        getReportData={() => ({
          centerName: user?.centerName || 'CenterPad',
          kpis: {
            totalClients: students.length,
            totalStudents: students.length,
            todayMeetings: kpiData.card2?.value || 0,
            todayLessons: kpiData.card3?.value || 0,
            activeGroups: groups.filter(g => g.isActive !== false).length,
            pendingSurveys: surveys.filter(s => s.isActive).length,
          },
          meetings: upcomingMeetings,
          lessons: upcomingLessons,
          activities: recentActivities,
        })}
      />

      {/* Modals */}
      <StudentSearchModal
        isOpen={showStudentSearchModal}
        onClose={() => {
          setShowStudentSearchModal(false);
          setSelectedSurvey(null);
        }}
        onSelect={handleStudentSelect}
        students={students}
        title={selectedSurvey ? t('calendar.selectClient', 'Select Client') : undefined}
        subtitle={selectedSurvey ? `${t('survey.fill', 'Fill Survey')}: ${selectedSurvey.title}` : undefined}
        showSkipButton={isInformedConsentSurvey(selectedSurvey)}
        onSkip={handleSkipClient}
      />

      {showLessonModal && (
        <Suspense fallback={null}>
          <LessonModal
            isOpen={showLessonModal}
            onClose={() => {
              setShowLessonModal(false);
              setEditingLesson(null);
            }}
            onSave={handleSaveLesson}
            students={students}
            groups={groups}
            teachers={teachers}
            lessons={upcomingLessons}
            user={user}
            editingLesson={editingLesson}
          />
        </Suspense>
      )}

      {showMeetingModal && (
        <Suspense fallback={null}>
          <MeetingModal
            isOpen={showMeetingModal}
            onClose={() => {
              setShowMeetingModal(false);
              setEditingMeeting(null);
            }}
            onSave={handleSaveMeeting}
            students={students}
            meetingTypes={meetingTypes}
            rooms={rooms}
            teachers={teachers}
            editingMeeting={editingMeeting}
            onRoomCreated={(newRoom) => setRooms(prev => [...prev, newRoom])}
          />
        </Suspense>
      )}

      {centerType === 'hair_transplant' && (
        <Suspense fallback={null}>
          <ScheduleTreatmentModal
            isOpen={showScheduleTreatmentModal}
            onClose={() => setShowScheduleTreatmentModal(false)}
            onSuccess={() => {
              setShowScheduleTreatmentModal(false);
              fetchUpcomingMeetings();
            }}
          />
        </Suspense>
      )}

      {(centerType === 'hair_transplant' || centerType === 'therapy') && (
        <Suspense fallback={null}>
          <UploadDocumentWizardModal
            isOpen={showUploadDocWizard}
            onClose={() => setShowUploadDocWizard(false)}
            centerType={centerType}
            prefetchedClients={students}
          />
        </Suspense>
      )}

      {centerType === 'therapy' && (
        <>
          {showGuestMeetingModal && (
            <Suspense fallback={null}>
              <GuestMeetingModal
                isOpen={showGuestMeetingModal}
                onClose={() => setShowGuestMeetingModal(false)}
                existingClients={students}
                workers={teachers}
                onSuccess={() => {
                  setShowGuestMeetingModal(false);
                  if (typeof fetchUpcomingMeetings === 'function') fetchUpcomingMeetings();
                }}
              />
            </Suspense>
          )}
          <ClientQuickAddModal
            isOpen={showNewClientModal}
            onClose={() => setShowNewClientModal(false)}
            endpoint="/students"
            onSuccess={(created) => {
              setShowNewClientModal(false);
              if (created?.id) navigate(`/students/${created.id}`);
            }}
          />
          <AddNoteBottomSheet
            isOpen={showAddNoteSheet}
            onClose={() => { setShowAddNoteSheet(false); setNoteSheetPreselected(null); }}
            // Sheet shows the "you owe a note" list when nothing is preselected
            // — this is the surface we actually want therapists to act on.
            meetings={pendingNotes.length > 0 ? pendingNotes : todayMeetingsList}
            preSelectedMeeting={noteSheetPreselected}
            onSuccess={() => {
              setShowAddNoteSheet(false);
              setNoteSheetPreselected(null);
              success(t('workerDashboard.noteSaved', 'Note saved'));
              fetchUpcomingMeetings().then((m) => setUpcomingMeetings(m || []));
            }}
            isMobile={isMobile}
            isRTL={isRTL}
            t={t}
          />
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
